import { Col, Form, InputNumber, Row, Select } from 'antd';
import { useGetExpensesDropDownFlag } from 'features/expenses-report/use-cases/get-dropdownReport';
import { FormSectionsSeparator } from 'features/users/components/modals/form-sections-separator';
import { useEffect, useState } from 'react';

const months = [
  'Jan',
  'Feb',
  'Mar',
  'Apr',
  'May',
  'Jun',
  'Jul',
  'Aug',
  'Sep',
  'Oct',
  'Nov',
  'Dec',
];

export const ExpensesReportInfoFormItems = ({
  form,
  editdata,
}: {
  form: any;
  editdata: any;
}) => {
  const [params, setParams] = useState<{
    Flag: string;
    facility_cascade_id?: string;
    field_cascade_id?: string;
    SearchKey?: string;
  }>({
    Flag: 'Normal',
  });
  const facilitySelected = Form.useWatch('facility_ids', form);
  const fieldSelected = Form.useWatch('field_id', form);
  const { data, isFetching } = useGetExpensesDropDownFlag(params as any, {
    enabled: !!params.Flag,
  });

  const [facilities, setFacilities] = useState([]);
  const [fields, setFields] = useState([]);
  const [sportnames, setSportnames] = useState([]);

  // Populate dropdown lists when data changes
  useEffect(() => {
    if (data?.items) {
      if (params.field_cascade_id) {
        setSportnames(data.items);
      } else if (params.facility_cascade_id) {
        setFields(data.items);
      } else {
        setFacilities(data.items);
      }
    }
  }, [data]);

  // Set form values if in edit mode
  useEffect(() => {
    if (editdata) {
      const monthValues = months.reduce((acc, month) => {
        acc[month.toLowerCase()] = editdata[month] || '';

        return acc;
      }, {} as Record<string, any>);

      form.setFieldsValue({
        ...monthValues,
        facility_ids: editdata.facility_id,
        field_id: editdata.field_id,
        sportIds: editdata.sports_id,
      });
    }
  }, [editdata, form]);

  const handleFacilityChange = (facilityId: string) => {
    form.setFieldsValue({ field_id: undefined, sportIds: undefined });
    setParams((prev) => ({
      ...prev,
      facility_cascade_id: facilityId,
      field_cascade_id: undefined,
      SearchKey: undefined,
    }));
  };
  const handleFacilitySearch = (input: string) => {
    setParams({
      Flag: 'Normal',
      SearchKey: input,
    });
  };
  const handleFieldSearch = (input: string) => {
    setParams((prev) => ({
      ...prev,
      SearchKey: input,
    }));
  };
  const handleSportnameSearch = (input: string) => {
    setParams((prev) => ({
      ...prev,
      SearchKey: input,
    }));
  };
  const handleFieldChange = (fieldId: string) => {
    form.setFieldsValue({ sportIds: undefined });
    setParams((prev) => ({
      ...prev,
      field_cascade_id: fieldId,
    }));
  };

  return (
    <FormSectionsSeparator title=''>
      {!editdata?.Id && (
        <Form.Item
          label='Select Facility'
          name='facility_ids'
          rules={[{ required: true, message: 'Please select a facility' }]}
        >
          <Select
            showSearch
            placeholder='Select Facility'
            loading={isFetching}
            onChange={handleFacilityChange}
            onSearch={handleFacilitySearch}
            filterOption={(input, option) =>
              (option?.label ?? '').toLowerCase().includes(input.toLowerCase())
            }
            options={facilities.map((f: any) => ({
              label: f.Text,
              value: f.Id,
            }))}
            allowClear
          />
        </Form.Item>
      )}

      {!editdata?.Id && (
        <Form.Item
          label='Select Field'
          name='field_id'
          rules={[{ required: true, message: 'Please select a field' }]}
        >
          <Select
            showSearch
            placeholder='Select Field'
            disabled={!facilitySelected || !fields.length}
            loading={isFetching}
            onChange={handleFieldChange}
            onSearch={handleFieldSearch}
            filterOption={(input, option) =>
              (option?.label ?? '').toLowerCase().includes(input.toLowerCase())
            }
            options={fields.map((f: any) => ({
              label: f.Text,
              value: f.Id,
            }))}
            allowClear
          />
        </Form.Item>
      )}

      {!editdata?.Id && (
        <Form.Item
          label='Sportname'
          name='sportIds'
          rules={[{ required: true, message: 'Please select a sportname' }]}
        >
          <Select
            placeholder='Select Sportname'
            onSearch={handleSportnameSearch}
            filterOption={(input, option) =>
              (option?.label ?? '').toLowerCase().includes(input.toLowerCase())
            }
            disabled={!facilitySelected || !fieldSelected || !sportnames.length}
            loading={isFetching}
            options={sportnames.map((s: any) => ({
              label: s.Text,
              value: s.Id,
            }))}
            allowClear
          />
        </Form.Item>
      )}

      <div style={{ marginTop: 24 }}>
        <label style={{ fontWeight: 500, display: 'block', marginBottom: 8 }}>
          Monthly
        </label>
        <Row gutter={[16, 16]}>
          {months.map((month) => (
            <Col span={8} key={month}>
              <Form.Item
                name={month.toLowerCase()}
                label={month}
                rules={[
                  {
                    type: 'number',
                    min: 0,
                    message: 'Value must be zero or positive',
                  },
                  {
                    validator: (_, value) => {
                      if (value === undefined || value === null)
                        return Promise.resolve();
                      const valueStr = value.toString();
                      const [, decimals] = valueStr.split('.');
                      if (decimals && decimals.length > 3) {
                        return Promise.reject(
                          'Maximum 3 decimal places allowed',
                        );
                      }

                      return Promise.resolve();
                    },
                  },
                ]}
              >
                <InputNumber
                  placeholder={`Enter value for ${month}`}
                  min={0}
                  precision={3}
                  style={{ width: '100%' }}
                  controls={false}
                  onKeyPress={(e) => {
                    const isNumber = /^[0-9]$/.test(e.key);
                    const isDot = e.key === '.';
                    const isBackspace = e.key === 'Backspace';
                    if (!isNumber && !isDot && !isBackspace) {
                      e.preventDefault();
                    }
                  }}
                />
              </Form.Item>
            </Col>
          ))}
        </Row>
      </div>
    </FormSectionsSeparator>
  );
};
